import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  HostListener,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

// ag-Grid imports
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowSelectionOptions,
} from 'ag-grid-community';
import { Textarea } from 'primeng/textarea';
import { DatePicker } from 'primeng/datepicker';
import { selectUser } from '@workspace-app/state/global';

// Application imports
import { PageComponent, User } from '@workspace-app/shared';
import { PermissionService } from '@workspace-app/shared/services/permission.service';
import { WorkflowNavigationService } from '@workspace-app/shared/services/workflow-navigation.service';
import { getLicenseRenewalWorkflowsColumns } from './license-renewals.columns';
import { LicenseRenewalWorkflowDataService } from '../../services/license-renewal-workflow-data.service';
import { LicenseRenewalWorkflowStore } from '../../state/license-renewal-workflow.store';

@Component({
  selector: 'app-license-extension-reviews',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PageComponent,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    FontAwesomeModule,
    AgGridModule,
    Textarea,
    DatePicker,
  ],
  templateUrl: './license-renewals.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./license-renewals.component.scss'],
})
export class LicenseRenewalWorkflowsComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private permissionService = inject(PermissionService);
  private store = inject(Store);
  private dataService = inject(LicenseRenewalWorkflowDataService);
  private workflowNavigation = inject(WorkflowNavigationService);

  licenseStore = inject(LicenseRenewalWorkflowStore);
  currentUser$: Observable<User>;
  currentUser: User | undefined;

  gridApi!: GridApi;
  createReviewForm!: FormGroup;
  showCreateDialog = false;

  currentYear = new Date().getFullYear();
  minDate = new Date();

  columnDefs: ColDef[] = [];

  gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      sortable: true,
      filter: true,
      enablePivot: false,
    },
    rowModelType: 'serverSide',
    pivotMode: false,
    columnMenu: 'new',
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true,
          },
        },
      ],
    },
    rowHeight: 48,
    pagination: true,
    paginationPageSize: 20,
    cacheBlockSize: 20,
    animateRows: true,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 100,
    },
    rowSelection: {
      mode: 'singleRow',
      checkboxes: false,
      enableClickSelection: true,
    } as RowSelectionOptions,
    getRowId: (params: GetRowIdParams) => params.data.id,
    onRowClicked: (event) => this.onRowClicked(event),
  };
  constructor() {
    this.currentUser$ = this.store.select(selectUser);

    // Sync dialog state with store
    effect(() => {
      this.showCreateDialog = this.licenseStore.showCreateDialog();
    });
  }

  ngOnInit(): void {
    // Always clear workflow navigation when we're at the reviews list table
    this.workflowNavigation.clearStack();

    this.initializeForm();

    // Subscribe to user changes
    this.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.currentUser = user;
    });

    // Initialize columns
    this.columnDefs = getLicenseRenewalWorkflowsColumns();
  }

  ngAfterViewInit(): void {
    // Also clear on view init to handle browser back button navigation
    this.workflowNavigation.clearStack();
  }

  initializeForm(): void {
    this.createReviewForm = this.fb.group({
      certificationYear: [this.currentYear + 1, [Validators.required, Validators.min(this.currentYear)]],
      proposedExpiryDate: [null, Validators.required],
      notes: [''],
    });

    // Set default expiry date to end of next year
    const defaultExpiryDate = new Date(this.currentYear + 1, 11, 31);
    this.createReviewForm.patchValue({ proposedExpiryDate: defaultExpiryDate });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;

    // Set up server-side datasource
    const datasource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams) => {
        this.dataService.getReviews(params);
      },
    };

    this.gridApi.setGridOption('serverSideDatasource', datasource);

    // Size columns to fit available space
    this.gridApi.sizeColumnsToFit();
  }

  adjustColumnSizes(): void {
    this.gridApi?.sizeColumnsToFit();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // Debounce resize to avoid excessive calls
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.adjustColumnSizes();
    }, 200);
  }

  private resizeTimeout: ReturnType<typeof setTimeout> | undefined;

  resetTableState(): void {
    // Reset filters only — the user's column layout (visibility, order, widths) is kept
    this.gridApi?.setFilterModel(null);
  }

  canCreateReview(): boolean {
    return this.permissionService.hasEffectivePermission(this.currentUser, 'Workflow.Write');
  }

  canViewReview(): boolean {
    return this.permissionService.hasEffectivePermission(this.currentUser, 'Workflow.Read');
  }

  canEditReview(): boolean {
    return this.permissionService.hasEffectivePermission(this.currentUser, 'Workflow.Write');
  }

  openCreateDialog(): void {
    this.showCreateDialog = true;
    this.licenseStore.openCreateDialog();
  }

  closeCreateDialog(): void {
    this.showCreateDialog = false;
    this.licenseStore.closeCreateDialog();
    this.createReviewForm.reset({
      certificationYear: this.currentYear + 1,
      proposedExpiryDate: new Date(this.currentYear + 1, 11, 31),
    });
  }

  createReview(): void {
    if (this.createReviewForm.valid) {
      const formValue = this.createReviewForm.value;

      this.licenseStore.createReview({
        certificationYear: formValue.certificationYear,
        proposedExpiryDate: formValue.proposedExpiryDate,
        notes: formValue.notes,
      });

      // Watch for completion and navigate
      const checkInterval = setInterval(() => {
        const reviewId = this.licenseStore.lastCreatedReviewId();
        const isLoading = this.licenseStore.activeReviewLoading();

        if (reviewId && !isLoading) {
          clearInterval(checkInterval);
          // Clear the lastCreatedReviewId to prevent re-navigation
          this.licenseStore.clearLastCreatedReviewId();
          // Refresh the grid to show the new review
          this.gridApi?.refreshServerSide();
          // Navigate to the new review
          this.router.navigate(['/certification/licenses/extension-reviews', reviewId]);
        }
      }, 100);

      // Timeout after 10 seconds to prevent infinite checking
      setTimeout(() => clearInterval(checkInterval), 10000);
    }
  }

  navigateToReview(reviewId: string): void {
    this.router.navigate(['/certification/licenses/extension-reviews', reviewId]);
  }

  onRowClicked(event: { data?: { id?: string } }): void {
    const review = event.data;
    if (review && review.id) {
      this.navigateToReview(review.id);
    }
  }
}
